import { computed, nextTick, reactive, ref, watch } from 'vue'
import type { LocationQuery } from 'vue-router'
import { COMPANY } from '@/constants/legalContent'

export type ServiceContractTab =
  | 'contract'
  | 'annex1'
  | 'annex2'
  | 'annex3'
  | 'annex4'
  | 'annex5'
  | 'summary'

export const SERVICE_CONTRACT_TABS: { id: ServiceContractTab; label: string; short: string }[] = [
  { id: 'contract', label: '기본계약서', short: '기본계약서' },
  { id: 'annex1', label: '별첨 1', short: '별첨 1' },
  { id: 'annex2', label: '별첨 2', short: '별첨 2' },
  { id: 'annex3', label: '별첨 3', short: '별첨 3' },
  { id: 'annex4', label: '별첨 4', short: '별첨 4' },
  /* 별첨 5(종료 인수인계)는 계약 체결이 아니라 해지 시점에 쓰는 서류라 탭에서 뺀다.
     종료 플로우가 생기면 다시 넣는다. */
  { id: 'summary', label: '요약본', short: '요약본' },
]

export type SelectState = 'selected' | 'unselected' | ''

export type ServiceContractForm = {
  /* 갑 */
  orgName: string
  facilityCode: string
  legalName: string
  representative: string
  bizNumber: string
  /** 우편번호 — 입력용만, 계약서 본문에는 넣지 않는다 */
  zipcode: string
  /** 주소1(도로명/지번) */
  address: string
  /** 주소2(상세) */
  addressDetail: string
  contactName: string
  contactEmail: string
  /* 을(함우리) — 기본값은 COMPANY, 계약서마다 수정 가능 */
  partyBLegalName: string
  partyBBizNumber: string
  partyBOwner: string
  partyBAddress: string
  partyBPhone: string
  partyBOrg: string
  /* 체결 */
  signedYear: string
  signedMonth: string
  signedDay: string
  partyASignName: string
  /* 갑 대표 전자서명(dataURL PNG, 빈 문자열이면 미서명) */
  partyASignImage: string
  partyBSignName: string
  annexAgreed: { a1: boolean; a2: boolean; a3: boolean; a4: boolean; a5: boolean }
  /* 별첨1 */
  wageSelect: SelectState
  wageStartYear: string
  wageStartMonth: string
  acctSelect: SelectState
  acctStartYear: string
  acctStartMonth: string
  contractEndYear: string
  contractEndMonth: string
  wageScopes: {
    baseInput: boolean
    payroll: boolean
    payslip: boolean
    insurance: boolean
    other: boolean
    otherText: string
  }
  acctScopes: {
    incomeExpense: boolean
    voucher: boolean
    books: boolean
    budget: boolean
    other: boolean
    otherText: string
  }
  specialist: 'none' | 'yes' | ''
  specialistDetail: string
  extraWork: string
  /* 별첨2 */
  wageFee: 'none' | 'flat' | 'headcount' | 'custom' | ''
  wageFlat: string
  wageBaseHeadcount: string
  wageExtraPerPerson: string
  wageCustomFormula: string
  acctFee: 'none' | 'flat' | 'volume' | 'custom' | ''
  acctFlat: string
  acctVolume: string
  acctCustomFormula: string
  monthlyTotal: string
  vatMode: 'included' | 'separate' | ''
  headcountDay: string
  headcountDef: string
  discount: string
  discountPeriod: string
  billingTiming: 'prepaid' | 'postpaid' | ''
  billDay: string
  dueDay: string
  payMethod: string
  bankAccount: string
  proration: 'daily' | 'monthly' | 'other' | ''
  prorationOther: string
  extraFeeRules: string
  lateFee: string
  refundPolicy: string
  /* 별첨2 작성 방식 — form: 양식, text: 자유 문구 */
  annex2Mode: 'form' | 'text'
  annex2Text: string
  wageFeeStartYear: string
  wageFeeStartMonth: string
  acctFeeStartYear: string
  acctFeeStartMonth: string
  feeAgreedYear: string
  feeAgreedMonth: string
  feeAgreedDay: string
  /* 별첨3 */
  wageSubmitDay: string
  wageProvideDay: string
  wageConfirmDay: string
  acctSubmitDay: string
  acctProvideDay: string
  acctConfirmDay: string
  deliveryMethod: string
  backupContact: string
  wageDocs: {
    contract: boolean
    attendance: boolean
    hr: boolean
    allowance: boolean
    account: boolean
    other: boolean
    otherText: string
  }
  acctDocs: {
    transactions: boolean
    evidence: boolean
    subsidy: boolean
    prevBooks: boolean
    other: boolean
    otherText: string
  }
  errorNotifyPath: string
  replyBusinessDays: string
  annex3PartyA: string
  annex3PartyB: string
  annex3ConfirmYear: string
  annex3ConfirmMonth: string
  annex3ConfirmDay: string
  /* 별첨4 */
  reconsignNone: boolean
  reconsign1: { vendor: string; work: string; items: string; agreedDate: string }
  reconsign2: { vendor: string; work: string; items: string; agreedDate: string }
  annex4AgreedYear: string
  annex4AgreedMonth: string
  annex4AgreedDay: string
  /* 별첨5 */
  terminateWage: boolean
  terminateAcct: boolean
  terminateAll: boolean
  terminateYear: string
  terminateMonth: string
  terminateDay: string
  terminateReason: string
  settleContact: string
  handover: {
    payroll: boolean
    wageBase: boolean
    books: boolean
    evidenceList: boolean
    other: boolean
    otherText: string
  }
  handoverMethod: 'standard' | 'secure' | 'other' | ''
  handoverMethodOther: string
  requestDate: string
  deliveredDate: string
  fileList: string
  specialConvert: 'none' | 'need' | ''
  specialConvertDoc: string
  settleStatus: 'done' | 'discussing' | ''
  settleNote: string
  accessEndDate: string
  preserveTargets: string
  destroyDate: string
  legalPreserve: string
  annex5PartyA: string
  annex5PartyB: string
  annex5ConfirmYear: string
  annex5ConfirmMonth: string
  annex5ConfirmDay: string
}

const STORAGE_KEY = 'jangi_service_contract_v1'

function emptyForm(): ServiceContractForm {
  return {
    orgName: '',
    facilityCode: '',
    legalName: '',
    representative: '',
    bizNumber: '',
    zipcode: '',
    address: '',
    addressDetail: '',
    contactName: '',
    contactEmail: '',
    partyBLegalName: COMPANY.legalName,
    partyBBizNumber: COMPANY.bizNumber,
    partyBOwner: COMPANY.owner,
    partyBAddress: COMPANY.address,
    partyBPhone: COMPANY.email,
    partyBOrg: '',
    signedYear: '',
    signedMonth: '',
    signedDay: '',
    partyASignName: '',
    partyASignImage: '',
    partyBSignName: COMPANY.owner,
    annexAgreed: { a1: false, a2: false, a3: false, a4: false, a5: false },
    wageSelect: '',
    wageStartYear: '',
    wageStartMonth: '',
    acctSelect: '',
    acctStartYear: '',
    acctStartMonth: '',
    contractEndYear: '',
    contractEndMonth: '',
    wageScopes: {
      baseInput: false,
      payroll: false,
      payslip: false,
      insurance: false,
      other: false,
      otherText: '',
    },
    acctScopes: {
      incomeExpense: false,
      voucher: false,
      books: false,
      budget: false,
      other: false,
      otherText: '',
    },
    specialist: '',
    specialistDetail: '',
    extraWork: '',
    wageFee: '',
    wageFlat: '',
    wageBaseHeadcount: '',
    wageExtraPerPerson: '',
    wageCustomFormula: '',
    acctFee: '',
    acctFlat: '',
    acctVolume: '',
    acctCustomFormula: '',
    monthlyTotal: '',
    vatMode: '',
    headcountDay: '',
    headcountDef: '',
    discount: '',
    discountPeriod: '',
    billingTiming: '',
    billDay: '',
    dueDay: '',
    payMethod: '',
    bankAccount: '',
    proration: '',
    prorationOther: '',
    extraFeeRules: '',
    lateFee: '',
    refundPolicy: '',
    annex2Mode: 'form',
    annex2Text: '',
    wageFeeStartYear: '',
    wageFeeStartMonth: '',
    acctFeeStartYear: '',
    acctFeeStartMonth: '',
    feeAgreedYear: '',
    feeAgreedMonth: '',
    feeAgreedDay: '',
    wageSubmitDay: '',
    wageProvideDay: '',
    wageConfirmDay: '',
    acctSubmitDay: '',
    acctProvideDay: '',
    acctConfirmDay: '',
    deliveryMethod: '',
    backupContact: '',
    wageDocs: {
      contract: false,
      attendance: false,
      hr: false,
      allowance: false,
      account: false,
      other: false,
      otherText: '',
    },
    acctDocs: {
      transactions: false,
      evidence: false,
      subsidy: false,
      prevBooks: false,
      other: false,
      otherText: '',
    },
    errorNotifyPath: '',
    replyBusinessDays: '',
    annex3PartyA: '',
    annex3PartyB: '',
    annex3ConfirmYear: '',
    annex3ConfirmMonth: '',
    annex3ConfirmDay: '',
    reconsignNone: false,
    reconsign1: { vendor: '', work: '', items: '', agreedDate: '' },
    reconsign2: { vendor: '', work: '', items: '', agreedDate: '' },
    annex4AgreedYear: '',
    annex4AgreedMonth: '',
    annex4AgreedDay: '',
    terminateWage: false,
    terminateAcct: false,
    terminateAll: false,
    terminateYear: '',
    terminateMonth: '',
    terminateDay: '',
    terminateReason: '',
    settleContact: '',
    handover: {
      payroll: false,
      wageBase: false,
      books: false,
      evidenceList: false,
      other: false,
      otherText: '',
    },
    handoverMethod: '',
    handoverMethodOther: '',
    requestDate: '',
    deliveredDate: '',
    fileList: '',
    specialConvert: '',
    specialConvertDoc: '',
    settleStatus: '',
    settleNote: '',
    accessEndDate: '',
    preserveTargets: '',
    destroyDate: '',
    legalPreserve: '',
    annex5PartyA: '',
    annex5PartyB: '',
    annex5ConfirmYear: '',
    annex5ConfirmMonth: '',
    annex5ConfirmDay: '',
  }
}

function mark(on: boolean) {
  return on ? '☑' : '☐'
}

function blank(value: string, width = 12) {
  const t = String(value || '').trim()
  if (t) return t
  return '　'.repeat(Math.min(width, 8))
}

function loadStored(): Partial<ServiceContractForm> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Partial<ServiceContractForm>
  } catch {
    return null
  }
}

export function parseContractTab(raw: unknown): ServiceContractTab {
  const id = String(raw || '')
  if (SERVICE_CONTRACT_TABS.some((t) => t.id === id)) return id as ServiceContractTab
  return 'contract'
}

function fillIfEmpty(form: ServiceContractForm, query: LocationQuery) {
  const org = String(query.org || '').trim()
  const code = String(query.code || '').trim()
  if (!form.orgName && org) form.orgName = org
  if (!form.facilityCode && code) form.facilityCode = code
  if (!form.wageSelect && (query.wage === '1' || query.wage === '0')) {
    form.wageSelect = query.wage === '1' ? 'selected' : 'unselected'
  }
  if (!form.acctSelect && (query.acct === '1' || query.acct === '0')) {
    form.acctSelect = query.acct === '1' ? 'selected' : 'unselected'
  }
}

function mergeForm(source: Partial<ServiceContractForm> | null | undefined): ServiceContractForm {
  const base = emptyForm()
  const src = source || {}
  const next: ServiceContractForm = {
    ...base,
    ...src,
    annexAgreed: { ...base.annexAgreed, ...(src.annexAgreed || {}) },
    wageScopes: { ...base.wageScopes, ...(src.wageScopes || {}) },
    acctScopes: { ...base.acctScopes, ...(src.acctScopes || {}) },
    wageDocs: { ...base.wageDocs, ...(src.wageDocs || {}) },
    acctDocs: { ...base.acctDocs, ...(src.acctDocs || {}) },
    reconsign1: { ...base.reconsign1, ...(src.reconsign1 || {}) },
    reconsign2: { ...base.reconsign2, ...(src.reconsign2 || {}) },
    handover: { ...base.handover, ...(src.handover || {}) },
  }
  if (next.annex2Mode !== 'text') next.annex2Mode = 'form'
  return next
}

export function useServiceContractForm(query: LocationQuery) {
  const form = reactive<ServiceContractForm>(mergeForm(loadStored()))
  /* 저장된 계약서를 통째로 갈아끼울 때 선택 해제 워처가 칸을 지우지 않게 */
  let hydrating = false
  /* 피계약자에게 보여 준 뒤에는 별첨 2 요금을 선택 해제 워처가 지우지 않는다 */
  const annex2Frozen = ref(false)

  /* 로컬 초안이 비어 있는 칸만 최소 쿼리로 채움 */
  fillIfEmpty(form, query)

  /* 미선택 서비스는 요금·종료 대상에서 자동으로 뺀다.
     문서 직접 편집·입력 시트 어느 경로로 바꿔도 같은 규칙이 적용되도록 모델에 둔다 */
  watch(
    () => form.wageSelect,
    (v) => {
      if (hydrating) return
      if (v === 'selected') {
        if (!annex2Frozen.value && (form.wageFee === 'none' || !form.wageFee)) form.wageFee = 'flat'
        return
      }
      if (v === 'unselected') {
        if (!annex2Frozen.value) form.wageFee = 'none'
        form.terminateWage = false
        form.wageStartYear = ''
        form.wageStartMonth = ''
      }
      /* 「선택한 경우에만 체크·작성」 칸들 — 선택을 풀면 비워 둔다 */
      const ws = form.wageScopes
      ws.baseInput = ws.payroll = ws.payslip = ws.insurance = ws.other = false
      ws.otherText = ''
      const wd = form.wageDocs
      wd.contract = wd.attendance = wd.hr = wd.allowance = wd.account = wd.other = false
      wd.otherText = ''
      form.wageSubmitDay = ''
      form.wageProvideDay = ''
      form.wageConfirmDay = ''
    },
  )

  watch(
    () => form.acctSelect,
    (v) => {
      if (hydrating) return
      if (v === 'selected') {
        if (!annex2Frozen.value && (form.acctFee === 'none' || !form.acctFee)) form.acctFee = 'flat'
        return
      }
      if (v === 'unselected') {
        if (!annex2Frozen.value) form.acctFee = 'none'
        form.terminateAcct = false
        form.acctStartYear = ''
        form.acctStartMonth = ''
      }
      const as = form.acctScopes
      as.incomeExpense = as.voucher = as.books = as.budget = as.other = false
      as.otherText = ''
      const ad = form.acctDocs
      ad.transactions = ad.evidence = ad.subsidy = ad.prevBooks = ad.other = false
      ad.otherText = ''
      form.acctSubmitDay = ''
      form.acctProvideDay = ''
      form.acctConfirmDay = ''
    },
  )

  /* 체크하지 않은 요금 방식의 금액 칸은 비워 둔다 — 문서에 유령 금액이 남지 않게 */
  watch(
    () => form.wageFee,
    (v) => {
      if (hydrating || annex2Frozen.value) return
      if (v !== 'flat') form.wageFlat = ''
      if (v !== 'headcount') {
        form.wageBaseHeadcount = ''
        form.wageExtraPerPerson = ''
      }
      if (v !== 'custom') form.wageCustomFormula = ''
    },
  )

  watch(
    () => form.acctFee,
    (v) => {
      if (hydrating || annex2Frozen.value) return
      if (v !== 'flat') form.acctFlat = ''
      if (v !== 'volume') form.acctVolume = ''
      if (v !== 'custom') form.acctCustomFormula = ''
    },
  )

  /* 「있음/기타」를 고른 경우에만 쓰는 칸들 */
  watch(
    () => form.specialist,
    (v) => {
      if (hydrating) return
      if (v !== 'yes') form.specialistDetail = ''
    },
  )
  watch(
    () => form.proration,
    (v) => {
      if (hydrating) return
      if (v !== 'other') form.prorationOther = ''
    },
  )
  watch(
    () => form.wageScopes.other,
    (v) => {
      if (hydrating) return
      if (!v) form.wageScopes.otherText = ''
    },
  )
  watch(
    () => form.acctScopes.other,
    (v) => {
      if (hydrating) return
      if (!v) form.acctScopes.otherText = ''
    },
  )
  watch(
    () => form.wageDocs.other,
    (v) => {
      if (hydrating) return
      if (!v) form.wageDocs.otherText = ''
    },
  )
  watch(
    () => form.acctDocs.other,
    (v) => {
      if (hydrating) return
      if (!v) form.acctDocs.otherText = ''
    },
  )

  /* 대표자명을 고치면 서명란의 을 성명도 같이 간다 */
  watch(
    () => form.partyBOwner,
    (v) => {
      if (hydrating) return
      if (form.partyBSignName !== v) form.partyBSignName = v
    },
  )

  let saveTimer: ReturnType<typeof setTimeout> | null = null
  watch(
    form,
    () => {
      if (hydrating) return
      if (saveTimer) clearTimeout(saveTimer)
      saveTimer = setTimeout(() => {
        try {
          const payload = { ...form }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
        } catch {
          /* quota 등 무시 */
        }
      }, 400)
    },
    { deep: true },
  )

  const filledCount = computed(() => {
    const keys: (keyof ServiceContractForm)[] = [
      'orgName',
      'facilityCode',
      'representative',
      'contactName',
      'wageSelect',
      'acctSelect',
    ]
    return keys.filter((k) => {
      const v = form[k]
      return typeof v === 'string' ? v.trim().length > 0 : Boolean(v)
    }).length
  })

  function replaceForm(source: Partial<ServiceContractForm> | null) {
    hydrating = true
    const next = mergeForm(source)
    Object.assign(form, next)
    nextTick(() => {
      hydrating = false
    })
  }

  function clearDraft() {
    replaceForm(null)
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* ignore */
    }
  }

  function displayDate(y: string, m: string, d: string) {
    if (!y && !m && !d) return '　　년　　월　　일'
    return `${blank(y, 4)}년 ${blank(m, 2)}월 ${blank(d, 2)}일`
  }

  function displayYm(y: string, m: string) {
    if (!y && !m) return '　　년　　월'
    return `${blank(y, 4)}년 ${blank(m, 2)}월`
  }

  /** 계약서 표시용 주소 — 우편번호 제외, 주소1+주소2만 */
  function displayAddress() {
    const line = [form.address, form.addressDetail].map((s) => String(s || '').trim()).filter(Boolean).join(' ')
    return line || blank('', 16)
  }

  return {
    form,
    company: COMPANY,
    filledCount,
    replaceForm,
    clearDraft,
    annex2Frozen,
    freezeAnnex2(on = true) {
      annex2Frozen.value = on
    },
    mark,
    blank,
    displayDate,
    displayYm,
    displayAddress,
  }
}
